'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from './utils';
import { reportSwallowed } from '@/lib/sentry';

function clearInteractionLocks() {
  try {
    document.querySelectorAll('[inert]').forEach((n) => n.removeAttribute('inert'));
    document.querySelectorAll('[aria-hidden="true"]').forEach((n) => n.removeAttribute('aria-hidden'));
    document.documentElement.removeAttribute('data-radix-scroll-lock');
    document.body.removeAttribute('data-radix-scroll-lock');
    document.body.classList.remove('react-remove-scroll-body');
    const html = document.documentElement as HTMLElement;
    const body = document.body as HTMLElement;
    html.style.overflow = '';
    body.style.overflow = '';
    body.style.position = '';
    body.style.width = '';
    body.style.paddingRight = '';
  } catch {
    // OBS-11 a-ignore: best-effort cleanup of stuck Radix overlays/locks; the nodes may already be gone.
  }
}

function Select(props: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup(props: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue(props: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = 'default',
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & { size?: 'sm' | 'default' }) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        'flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border bg-card px-3 py-2 text-sm shadow-sm',
        'border-input/90 transition-[color,box-shadow,border-color] outline-none',
        'focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:border-ring',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
        'dark:bg-input/30 dark:hover:bg-input/50',
        'data-[size=default]:h-9 data-[size=sm]:h-8',
        "[&_svg:not([class*='text-'])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        '*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  // force popper to avoid body-level overlay semantics
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  // Compose handlers: call user-provided first, then our cleanup
  const handleCloseAutoFocus = React.useCallback<
    NonNullable<React.ComponentProps<typeof SelectPrimitive.Content>['onCloseAutoFocus']>
  >(
    (e) => {
      try {
        (props as any)?.onCloseAutoFocus?.(e);
      } catch (err) {
        // OBS-11: the UI degrades and carries on, but the failure is reported (once per page load).
        reportSwallowed('Select.onCloseAutoFocus', err);
      }
      // prevent focus steal and clear any stray locks right after close
      e?.preventDefault?.();
      requestAnimationFrame(clearInteractionLocks);
    },
    [props],
  );

  const handleEscape = React.useCallback<
    NonNullable<React.ComponentProps<typeof SelectPrimitive.Content>['onEscapeKeyDown']>
  >(
    (e) => {
      try {
        (props as any)?.onEscapeKeyDown?.(e);
      } catch (err) {
        // OBS-11: the UI degrades and carries on, but the failure is reported (once per page load).
        reportSwallowed('Select.onEscapeKeyDown', err);
      }
      requestAnimationFrame(clearInteractionLocks);
    },
    [props],
  );

  const handlePointerDownOutside = React.useCallback<
    NonNullable<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>['onPointerDownOutside']>
  >(
    (e) => {
      try {
        (props as any)?.onPointerDownOutside?.(e);
      } catch (err) {
        // OBS-11: the UI degrades and carries on, but the failure is reported (once per page load).
        reportSwallowed('Select.onPointerDownOutside', err);
      }
      // run a tiny bit later so Radix unmounts first
      setTimeout(clearInteractionLocks, 0);
    },
    [props],
  );

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        // keep panel within the editor stacking context
        position="popper"
        sideOffset={6}
        avoidCollisions
        collisionPadding={8}
        className={cn(
          'bg-popover text-popover-foreground z-[80] min-w-[8rem] rounded-md border shadow-md',
          'max-h-[var(--radix-select-content-available-height)] overflow-x-hidden overflow-y-auto p-0',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'origin-[var(--radix-select-content-transform-origin)]',
          'pointer-events-auto',
          className,
        )}
        onCloseAutoFocus={handleCloseAutoFocus}
        onEscapeKeyDown={handleEscape}
        onPointerDownOutside={handlePointerDownOutside}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1 h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('px-2 py-1.5 text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm select-none outline-none',
        'focus:bg-accent focus:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        "[&_svg:not([class*='text-'])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        '*:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2',
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('pointer-events-none -mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
